import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { abTestStepType } from "./ab-test";
import {
  recognizeEmailStepType,
  recognizeOmsActionStepType,
} from "./action-inspector";
import { blockCheckoutStepType } from "./block-checkout/block-checkout";
import { checkoutExperienceStepType } from "./checkout-experience/checkout-experience";
import { conciergeBlockStepType } from "./concierge-block";
import { conditionStepType } from "./condition";
import { doNothingStepType } from "./do-nothing";
import { downsellStepType } from "./downsell";
import { hsaConditionStepType } from "./hsa/hsa-condition";
import { hsaContentStepType } from "./hsa/hsa-content";
import { hsaEndStepType } from "./hsa/hsa-end";
import { hsaKnockoutStepType } from "./hsa/hsa-knockout";
import { hsaQuestionGroupStepType } from "./hsa/hsa-question-group";
import { FrontendStepType } from "./index";
import { manageCustomerTagsStepType } from "./manage-customer-tags";
import { manageStaticSegmentStepType } from "./manage-static-segment";
import { omsCodeStepType } from "./oms/oms-code";
import { omsConditionStepType } from "./oms/oms-condition";
import { postPurchasePageStepType } from "./post-purchase-page/post-purchase-page";
import { postPurchaseSegmentStepType } from "./post-purchase-segment/post-purchase-segment";
import { postPurchaseSplitStepType } from "./post-purchase-split/post-purchase-split";
import { processReturnStepType } from "./process-return";
import { rcoCheckoutPackageStepType } from "./rco-checkout-package";
import { rcoCheckoutPackageConditionStepType } from "./rco-checkout-package-condition";
import { rcoSegmentStepType } from "./rco-segment/rco-segment";
import { rcoSplitStepType } from "./rco-split/rco-split";
import { recoverStepType } from "./recover";
import { ruleActionsStepType } from "./rules/rule-actions-step";
import { ruleConditionsStepType } from "./rules/rule-conditions-step";
import { ruleConditionsV3StepType } from "./rules/rule-conditions-v3-step";
import { sendEmailStepType } from "./send-email";
import { sendSmsStepType } from "./send-sms";
import { sendWebhookStepType } from "./send-webhook";
import { shopperAgentMessageStepType } from "./shopper-agent-message";
import { suppliesShippingConditionStepType } from "./supplies/supplies-shipping-condition";
import { suppliesShippingRateStepType } from "./supplies/supplies-shipping-rate";
import { trackingPageStepType } from "./tracking-page/tracking-page";
import { triggerStepType } from "./trigger";
import { upsellStepType } from "./upsell";
import { upsellConditionStepType } from "./upsell-condition";
import { waitStepType } from "./wait";
import { waitForEventStepType } from "./wait-for-event";
import { workOrderConditionStepType } from "./work-order/work-order-condition";
import { workOrderWorkStepType } from "./work-order/work-order-work-step";

export const stepTypes: Record<StepType, FrontendStepType<any>> = {
  [StepType.DO_NOTHING]: doNothingStepType,
  [StepType.WAIT]: waitStepType,
  [StepType.WAIT_FOR_EVENT]: waitForEventStepType,
  [StepType.CONDITION]: conditionStepType,
  [StepType.SEND_SMS]: sendSmsStepType,
  [StepType.SEND_EMAIL]: sendEmailStepType,
  [StepType.RECOGNIZE_EMAIL]: recognizeEmailStepType,
  [StepType.SEND_WEBHOOK]: sendWebhookStepType,
  [StepType.MANAGE_CUSTOMER_TAGS]: manageCustomerTagsStepType,
  [StepType.MANAGE_STATIC_SEGMENT]: manageStaticSegmentStepType,
  [StepType.RECOVER]: recoverStepType,
  [StepType.TRIGGER]: triggerStepType,
  [StepType.UPSELL]: upsellStepType,
  [StepType.DOWNSELL]: downsellStepType,
  [StepType.UPSELL_CONDITION]: upsellConditionStepType,
  [StepType.OMS_ACTION]: recognizeOmsActionStepType,
  [StepType.OMS_CONDITION]: omsConditionStepType,
  [StepType.OMS_CODE]: omsCodeStepType,
  [StepType.AB_TEST]: abTestStepType,

  // Checkout tree
  [StepType.RCO_SPLIT]: rcoSplitStepType,
  [StepType.CHECKOUT_EXPERIENCE]: checkoutExperienceStepType,
  [StepType.RCO_SEGMENT]: rcoSegmentStepType,
  [StepType.BLOCK_CHECKOUT]: blockCheckoutStepType,

  // Checkout packages
  [StepType.RCO_CHECKOUT_PACKAGE]: rcoCheckoutPackageStepType,
  [StepType.RCO_CHECKOUT_PACKAGE_CONDITION]:
    rcoCheckoutPackageConditionStepType,

  // Post-purchase
  [StepType.POST_PURCHASE_PAGE]: postPurchasePageStepType,
  [StepType.POST_PURCHASE_SEGMENT]: postPurchaseSegmentStepType,
  [StepType.POST_PURCHASE_SPLIT]: postPurchaseSplitStepType,

  // Tracking page
  [StepType.TRACKING_PAGE]: trackingPageStepType,

  // Support rules
  [StepType.RULE_CONDITIONS]: ruleConditionsStepType,
  [StepType.RULE_ACTIONS]: ruleActionsStepType,
  [StepType.RULE_CONDITIONS_V3]: ruleConditionsV3StepType,
  [StepType.RULE_ACTIONS_V3]: ruleActionsStepType,

  // Work order flows
  [StepType.WORK_ORDER_WORK_STEP]: workOrderWorkStepType,
  [StepType.WORK_ORDER_CONDITION]: workOrderConditionStepType,

  // Return processing flows
  [StepType.PROCESS_RETURN]: processReturnStepType,

  // Compensation and restocking flows: the new editor (AdvancedFlowEditorShell)
  // handles these step types; reuse doNothingStepType here so the AdvancedFlow
  // editor's step registry remains exhaustive even though these step types are
  // never reachable from the legacy editor.
  [StepType.COMPENSATION_STEP]: doNothingStepType,
  [StepType.RESTOCKING_STEP]: doNothingStepType,
  [StepType.RETURN_FLOW_CONDITION]: doNothingStepType,

  // Order tracking automations
  [StepType.CONCIERGE_BLOCK]: conciergeBlockStepType,
  [StepType.SHOPPER_AGENT_MESSAGE]: shopperAgentMessageStepType,

  // Supplies shipping rules
  [StepType.SUPPLIES_SHIPPING_CONDITION]: suppliesShippingConditionStepType,
  [StepType.SUPPLIES_SHIPPING_RATE]: suppliesShippingRateStepType,

  // HSA questionnaire flow view (read-only inspectors)
  [StepType.HSA_QUESTION_GROUP]: hsaQuestionGroupStepType,
  [StepType.HSA_CONDITION]: hsaConditionStepType,
  [StepType.HSA_KNOCKOUT]: hsaKnockoutStepType,
  [StepType.HSA_CONTENT]: hsaContentStepType,
  [StepType.HSA_END]: hsaEndStepType,
};
